import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Heart, Users, Star, Target, Info, Lightbulb, Compass } from 'lucide-react';
import { motion } from 'framer-motion';
import { BackButton } from '@/components/ui/back-button';

export default function AboutPage() {
  const values = [
    {
      icon: <Heart className="w-6 h-6 text-pink-500" />,
      title: "Bienveillance",
      description: "Nous accompagnons chaque personne avec empathie et respect dans son parcours énergétique."
    },
    {
      icon: <Star className="w-6 h-6 text-yellow-500" />,
      title: "Excellence",
      description: "Nous nous engageons à fournir des outils précis et des conseils personnalisés de haute qualité."
    },
    {
      icon: <Target className="w-6 h-6 text-blue-500" />,
      title: "Innovation",
      description: "Nous développons constamment nos outils pour offrir la meilleure expérience possible."
    },
    {
      icon: <Compass className="w-6 h-6 text-green-500" />,
      title: "Alignement",
      description: "Nous croyons que l'alignement énergétique est la clé d'une vie épanouissante et équilibrée."
    }
  ];

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-8">
        <BackButton to="/" />
        
        <Card className="backdrop-blur-sm bg-white/90">
          <CardContent className="p-8 space-y-8">
            <div className="flex items-center gap-3 mb-4">
              <Info className="w-8 h-8 text-[#54075B]" />
              <h1 className="text-3xl font-bold">À Propos du Human & Entity Design</h1>
            </div>

            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="prose max-w-none"
            >
              <p className="text-lg text-gray-700 leading-relaxed">
                Le Human & Entity Design (HED) est une approche innovante qui combine science, spiritualité et sagesse ancestrale pour révéler les énergies qui vous définissent. Notre mission est d'accompagner chaque individu dans la découverte de son potentiel énergétique et de sa véritable essence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mt-4">
                Nous croyons que l'équilibre intérieur est la clé d'une vie épanouissante, et notre objectif est de fournir des outils simples, précis et accessibles pour aider chacun à comprendre, harmoniser et aligner son énergie.
              </p>
            </motion.div>

            {/* Notre Approche */}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-[#54075B] flex items-center gap-2">
                <Lightbulb className="w-6 h-6" />
                Notre Approche
              </h2>
              <p className="text-gray-700">
                Le HED offre une compréhension profonde de votre design énergétique unique, vous permettant de :
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Découvrir vos forces et talents uniques</li>
                <li>Identifier les déséquilibres énergétiques pour mieux les surmonter</li>
                <li>Suivre des recommandations pratiques adaptées à vos besoins spécifiques</li>
                <li>Observer votre évolution au fil du temps grâce à des outils personnalisés</li>
                <li>Prendre des décisions alignées avec votre nature profonde</li>
              </ul>
            </div>

            {/* Nos Valeurs */}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-[#54075B]">Nos Valeurs</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.2 }}
                    className="p-6 bg-white rounded-lg shadow-sm"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {value.icon}
                      <h3 className="font-semibold text-lg">{value.title}</h3>
                    </div>
                    <p className="text-gray-600">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Notre Histoire */}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-[#54075B]">Notre Histoire</h2>
              <p className="text-gray-700">
                Le Human & Entity Design est né de la vision du Cabinet Immersion 7, qui a développé une approche unique combinant différentes traditions de sagesse avec des connaissances modernes en psychologie, neurosciences et physique quantique.
              </p>
              <p className="text-gray-700">
                Notre équipe de praticiens et développeurs passionnés travaille constamment à l'amélioration de nos outils et méthodologies pour offrir une expérience toujours plus précise et personnalisée.
              </p>
            </div>

            {/* Notre Engagement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-[#54075B]/5 p-6 rounded-lg"
            >
              <h2 className="text-xl font-semibold text-[#54075B] mb-4">Notre Engagement</h2>
              <p className="text-gray-700">
                En mettant la puissance de la technologie au service de la connaissance de soi, nous visons à simplifier ce qui peut sembler complexe et à rendre chaque expérience enrichissante. Que vous soyez en quête d'équilibre, de clarté ou de transformation, nous sommes là pour vous guider pas à pas, dans la bienveillance et l'écoute.
              </p>
            </motion.div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}